<template>
    <span class="rating-stars" :class="`rating-stars-${size}`">
        <span v-for="(state, index) in starStates" :key="index" class="rating-star">
            <svg class="rating-star-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
                <defs>
                    <linearGradient :id="gradientId(index)" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" class="rating-star-active-stop" />
                        <stop :offset="`${fillPercent(state)}%`" class="rating-star-active-stop" />
                        <stop :offset="`${fillPercent(state)}%`" class="rating-star-empty-stop" />
                        <stop offset="100%" class="rating-star-empty-stop" />
                    </linearGradient>
                </defs>
                <path :d="starPath" :fill="`url(#${gradientId(index)})`" />
            </svg>
        </span>
    </span>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue'

type RatingValue = number | string | null | undefined
type RatingSize = 'sm' | 'md'
type StarState = 'full' | 'half' | 'empty'

const props = withDefaults(defineProps<{
    rating: RatingValue,
    max?: number,
    size?: RatingSize
}>(), {
    max: 5,
    size: 'md'
})

const starPath = 'M10 0L13.09 6.26L20 7.27L15 12.14L16.18 19.02L10 15.77L3.82 19.02L5 12.14L0 7.27L6.91 6.26L10 0Z'
const componentUid = getCurrentInstance()?.uid ?? Math.random().toString(36).slice(2)

const normalizedRating = computed(() => {
    if (props.rating === null || props.rating === undefined || props.rating === '') return 0
    const value = Number(props.rating)
    if (!Number.isFinite(value)) return 0
    return Math.min(Math.max(value, 0), props.max)
})

const starStates = computed<StarState[]>(() => Array.from({ length: props.max }, (_, index) => {
    const starIndex = index + 1
    const diff = normalizedRating.value - (starIndex - 1)
    if (diff >= 0.75) return 'full'
    if (diff >= 0.25) return 'half'
    return 'empty'
}))

const fillPercent = (state: StarState) => {
    if (state === 'full') return 100
    if (state === 'half') return 50
    return 0
}

const gradientId = (index: number) => `rating-star-${componentUid}-${index}`
</script>

<style scoped>
.rating-stars {
    display: inline-flex;
    align-items: center;
    gap: 1px;
}

.rating-star {
    position: relative;
    display: inline-flex;
    width: var(--rating-star-size);
    height: var(--rating-star-size);
    overflow: hidden;
}

.rating-stars-sm {
    --rating-star-size: 10px;
}

.rating-stars-md {
    --rating-star-size: var(--employee-rating-star-size, 12px);
}

.rating-star-icon {
    width: 100%;
    height: 100%;
}

.rating-star-active-stop {
    stop-color: var(--employee-rating-star-color, #FED36B);
}

.rating-star-empty-stop {
    stop-color: var(--color-text-ghost, #C9C9C9);
}
</style>
